import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { type FC, type ReactNode, useState } from "react";

import { cn } from "@/lib/utils";
import { m } from "@recobra/i18n/messages";
import { Button } from "@recobra/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@recobra/ui/collapsible";

import {
  CHECKLIST_ROW_GRID,
  CHECKLIST_ROW_SPACING,
  ChecklistCheckIcon,
  ChecklistRowDeepLink,
  type ChecklistRowLink,
} from "./ChecklistRow";

/**
 * A checklist row whose completion is derived from a set of nested rows
 * (rendered as `children`), expanded with the toggle at the end of the row.
 * Domain-agnostic: the caller decides which rows to group, the title, the
 * link, and whether it starts open.
 */
export const ChecklistRowGroup: FC<{
  title: string;
  satisfied: boolean;
  link?: ChecklistRowLink | null;
  disabled: boolean;
  /** Starts expanded instead of collapsed — for a group whose items need attention as soon as the task opens. */
  defaultOpen?: boolean;
  forceOpen?: boolean;
  actions?: ReactNode;
  children: ReactNode;
  /** The link navigates away from the modal's own route — close it so it doesn't linger over the destination. */
  onNavigate?: () => void;
}> = ({
  title,
  satisfied,
  link,
  disabled,
  defaultOpen = false,
  forceOpen = false,
  actions,
  children,
  onNavigate,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const expanded = open || forceOpen;
  const ExpanderIcon = expanded ? ChevronUpIcon : ChevronDownIcon;
  const effectiveLink = link && { ...link, disabled };

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setOpen}
      render={<li className={cn("flex flex-col", CHECKLIST_ROW_SPACING)} />}
    >
      <div className={CHECKLIST_ROW_GRID}>
        <ChecklistCheckIcon satisfied={satisfied} className="col-start-1 self-center" />
        <span className="col-start-2 self-center text-xs">{title}</span>
        <div className="col-start-3 flex min-h-6 items-center justify-end gap-3 self-center">
          {effectiveLink ? (
            <ChecklistRowDeepLink link={effectiveLink} onNavigate={onNavigate} />
          ) : null}
          {actions}
          {forceOpen ? null : (
            <CollapsibleTrigger
              render={<Button variant="ghost" size="icon-xs" />}
              aria-label={
                expanded
                  ? m.checklist_group_collapse({ group: title })
                  : m.checklist_group_expand({ group: title })
              }
            >
              <ExpanderIcon />
            </CollapsibleTrigger>
          )}
        </div>
      </div>
      <CollapsibleContent
        render={
          <ul className="mt-1 flex h-(--collapsible-panel-height) flex-col overflow-hidden transition-[height] duration-200 ease-out data-ending-style:h-0 data-starting-style:h-0 [&>li]:py-1 [&>li]:pl-7" />
        }
      >
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};
